import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateMyProfile } from "@/services/profile";
import { trackOnboardingProfileSaved } from "@/analytics/analytics";
import { US_STATES, CA_PROVINCES } from "./locations";
import { ChoiceCard, OnboardingQuestion, SelectionSection } from "./OnboardingQuestion";
import type { OnboardingStepProps } from "./types";

interface BasicsValues {
  displayName: string;
  gradYear: string;
  homeState: string;
  gender: string;
}

interface BasicsErrors {
  displayName?: string;
  gradYear?: string;
  homeState?: string;
  gender?: string;
}

function validate(values: BasicsValues): { ok: boolean; errors: BasicsErrors } {
  const errors: BasicsErrors = {};

  if (values.displayName.trim().length < 2) {
    errors.displayName = "Name must be at least 2 characters";
  }

  const yr = Number(values.gradYear);
  if (values.gradYear.length !== 4 || !Number.isInteger(yr) || yr < 2024 || yr > 2035) {
    errors.gradYear = "Enter a valid year (2024–2035)";
  }

  if (!values.homeState) {
    errors.homeState = "Please select your state";
  }

  if (!values.gender) {
    errors.gender = "Please select gender";
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Step 1 — collects the four core player fields (name, grad year, home state,
 * gender). Field patterns and validation mirror `ProfileSection` in
 * `JgsLinkBanner`. Reports validity to the wizard and registers a save fn that
 * persists via `updateMyProfile` when the user clicks Continue.
 */
export default function PlayerBasicsStep({
  profile,
  onValidityChange,
  registerSave,
  updateProfile,
}: OnboardingStepProps) {
  const isPlayer = profile.onboardingRole !== "supporter";
  const [displayName, setDisplayName] = useState(profile.displayName ?? "");
  const [gradYear, setGradYear] = useState(profile.gradYear ? String(profile.gradYear) : "");
  const [homeState, setHomeState] = useState(profile.state ?? "");
  const [gender, setGender] = useState(profile.gender ?? "");
  const [errors, setErrors] = useState<BasicsErrors>({});

  const values: BasicsValues = { displayName, gradYear, homeState, gender };
  const isValid = validate(values).ok;

  // Report validity so the wizard can enable/disable Continue.
  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  // Register a stable save fn that reads the latest values via a ref, so the
  // wizard always calls the current values without re-registering each render.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    registerSave(async () => {
      const current = valuesRef.current;
      const result = validate(current);
      if (!result.ok) {
        setErrors(result.errors);
        return false;
      }
      try {
        await updateMyProfile({
          displayName: current.displayName.trim(),
          gradYear: Number(current.gradYear),
          state: current.homeState,
          gender: current.gender,
        });
        trackOnboardingProfileSaved({ hasGpa: false, hasSat: false });
        // Carry the saved answers forward so later steps (JGS prefill,
        // school typeahead gender) see them without a refetch.
        updateProfile?.({
          displayName: current.displayName.trim(),
          gradYear: Number(current.gradYear),
          state: current.homeState,
          gender: current.gender,
        });
        return true;
      } catch (err: any) {
        toast.error(String(err?.message || "Failed to save"));
        return false;
      }
    });
    return () => registerSave(null);
  }, [registerSave, updateProfile]);

  const handleGradYearChange = (value: string) => {
    setGradYear(value.replace(/\D/g, "").slice(0, 4));
  };

  return (
    <OnboardingQuestion
      eyebrow="Player profile"
      title={isPlayer ? "Now, tell me about yourself." : "Now, tell me about the golfer."}
      description="These basics help me search the right teams, rankings, and recruiting timelines."
      coachCopy="We’ll use this information throughout your plan, so you won’t have to enter it again."
      coachBadge="01"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-bold text-forest-soft">
              {isPlayer ? "Your name" : "Golfer’s name"} <span className="text-destructive">*</span>
            </label>
            <Input
              className="h-12 rounded-xl border-border-strong bg-white px-4 text-forest shadow-sm focus-visible:ring-primary"
              placeholder={isPlayer ? "Your first and last name" : "Golfer's first and last name"}
              value={displayName}
              onChange={e => {
                setDisplayName(e.target.value);
                setErrors(p => ({ ...p, displayName: undefined }));
              }}
            />
            {errors.displayName && (
              <p className="mt-1 text-xs text-destructive">{errors.displayName}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-forest-soft">
              Graduation year <span className="text-destructive">*</span>
            </label>
            <Input
              className="h-12 rounded-xl border-border-strong bg-white px-4 text-forest shadow-sm focus-visible:ring-primary"
              placeholder="e.g. 2028"
              value={gradYear}
              onChange={e => {
                handleGradYearChange(e.target.value);
                setErrors(p => ({ ...p, gradYear: undefined }));
              }}
              inputMode="numeric"
            />
            {errors.gradYear && <p className="mt-1 text-xs text-destructive">{errors.gradYear}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-bold text-forest-soft">
              Home state or province <span className="text-destructive">*</span>
            </label>
            <Select
              value={homeState}
              onValueChange={v => {
                setHomeState(v);
                setErrors(p => ({ ...p, homeState: undefined }));
              }}
            >
              <SelectTrigger className="h-12 rounded-xl border-border-strong bg-white px-4 text-forest shadow-sm focus:ring-primary">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>United States</SelectLabel>
                  {US_STATES.map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Canada</SelectLabel>
                  {CA_PROVINCES.map(p => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {errors.homeState && (
              <p className="mt-1 text-xs text-destructive">{errors.homeState}</p>
            )}
          </div>
        </div>

        <SelectionSection
          label="Which college golf program should I search?"
          help="This filters teams and rankings to the correct program."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { value: "male", title: "Men’s golf", icon: "M" },
              { value: "female", title: "Women’s golf", icon: "W" },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                selected={gender === option.value}
                onClick={() => {
                  setGender(option.value);
                  setErrors(previous => ({ ...previous, gender: undefined }));
                }}
                icon={option.icon}
                title={option.title}
              />
            ))}
          </div>
          {errors.gender && <p className="mt-1 text-xs text-destructive">{errors.gender}</p>}
        </SelectionSection>
      </div>
    </OnboardingQuestion>
  );
}
